import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, DatePicker, Select, InputNumber, Dropdown, Menu } from 'antd';
import moment from 'moment';
import classnames from 'classnames';
import {
  FormComponentProps,
  FormItemProps,
} from "@ant-design/compatible/lib/form";
import TextAreaRef from 'antd/lib/input/TextArea';

import useKeydown from '../../hooks/use-keydown';

/** Form.Item 兼容类型：@ant-design/compatible 的 FormItemProps 未声明 children */
const FormItem = Form.Item as React.ComponentType<FormItemProps & { children?: React.ReactNode }>;
import useUpgrade from '../../hooks/use-upgrade';

import style from './index.less';
import _ from 'lodash';

const { Option } = Select;
const { TextArea } = Input;

const phoneCheckTextMap: { [key: string]: string } = {
  '0': '空号',
  '1': '正常',
  '2': '停机',
  '3': '空号',
  '4': '沉默号',
  '5': '风险号'
};

const phoneCheckColorMap: { [key: string]: string } = {
  '0': '#ff4236',
  '1': '#15c292',
  '2': '#ff8806',
  '3': '#ff4236',
  '4': '#1880ff',
  '5': 'black'
};

interface ItemRowProps {
  label?: string;
  children: JSX.Element | string;
  contentClass?: string;
  tel?: string;
  phoneResult?: any;
  onSpaceCheck?: (tel: string) => void;
}

const ItemRow = ({
  label = '',
  children = '',
  contentClass = '',
  tel = '',
  phoneResult,
  onSpaceCheck
}: ItemRowProps): JSX.Element => {
  const title = _.isString(children) ? children : '';

  const handleSpaceCheck = () => {
    if (onSpaceCheck) onSpaceCheck(tel);
  };

  // 有手机号的时候并且符合手机号正则
  const mobileRegex = /^1[3-9]\d{9}$/;
  if (tel && mobileRegex.test(tel)) {
    // 判断是否有检测结果
    let hasResult = false;
    if (phoneResult && phoneResult.status && phoneResult.status.length > 0) {
      hasResult = true;
    }
    const menu = () => {
      if (!hasResult) {
        return <></>;
      }
      return (
        <Menu
          items={[
            {
              key: 'spaceCheckResult',
              className: style['spaceCheckResult'],
              label: (
                <>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div>{`空号检测结果：`}</div>
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: phoneCheckColorMap[phoneResult.status]
                      }}
                    ></div>
                    <div style={{ marginLeft: '3px' }}>{phoneCheckTextMap[phoneResult.status]}</div>
                  </div>
                  <div>{`号码归属地：${phoneResult.area}`}</div>
                  <div>{`检测时间：${moment(phoneResult['check_at'] * 1000).format('YYYY-MM-DD HH:mm:ss')}`}</div>
                  <a style={{ color: '#1880FF', fontSize: '11px' }} onClick={handleSpaceCheck}>
                    重新检测
                  </a>
                </>
              )
            }
          ]}
        />
      );
    };

    return (
      <div className={style['item-row']}>
        <label title={label}>{String(label ?? '')}</label>
        <div
          className={classnames(style.content, { [contentClass]: contentClass })}
          style={{ display: 'flex', alignItems: 'center' }}
          title={title}
        >
          {children}
          {hasResult && (
            <Dropdown trigger={['hover']} dropdownRender={menu} placement='top'>
              <a
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: phoneCheckColorMap[phoneResult.status],
                  marginLeft: '5px'
                }}
              ></a>
            </Dropdown>
          )}
        </div>
        {!hasResult && (
          <a style={{ marginLeft: '5px', color: '#1880FF', fontSize: '11px' }} onClick={handleSpaceCheck}>
            检测
          </a>
        )}
      </div>
    );
  }

  return (
    <div className={style['item-row']}>
      <label title={label}>{String(label ?? '')}</label>
      <div className={classnames(style.content, { [contentClass]: contentClass })} title={title}>
        {children}
      </div>
    </div>
  );
};

interface VisitorInfoListProps {
  data: Array<ItemRowProps>;
  attrs?: any;
  className?: string;
  onSpacePhoneCheck?: (tel: string) => void;
}
const VisitorInfoList = ({ data, attrs, className, onSpacePhoneCheck }: VisitorInfoListProps): JSX.Element => {
  const handleGetPhoneAndResult = () => {
    if (attrs) {
      const phone = attrs['tel'];
      const phoneResult = attrs['phone_result'];
      return {
        tel: phone,
        phone_result: phoneResult
      };
    } else {
      return {
        tel: '',
        phone_result: { area: '', carrier: '', check_at: 0, status: '' }
      };
    }
  };

  const handleSpaceCheck = (tel: string) => {
    if (onSpacePhoneCheck) {
      onSpacePhoneCheck(tel);
    }
  };

  return (
    <div className={classnames(style['list-box'], className)}>
      {data.map((val, index) => {
        if (val.label === '电话') {
          const result = handleGetPhoneAndResult();
          return (
            <ItemRow
              {...val}
              key={`${val.label}_${index}`}
              tel={result.tel}
              phoneResult={result.phone_result}
              onSpaceCheck={handleSpaceCheck}
            />
          );
        }
        if (val.label === '企业微信') {
          return (
            <ItemRow key={`${val.label}_${index}`} label={val.label}>
              {attrs ? (
                attrs?.wecom_link_id ? (
                  <a
                    target='_blank'
                    href={`https://work.weixin.qq.com/ca/${attrs?.wecom_link_id}`}
                    style={{ display: 'inline-block' }}
                  >
                    {attrs?.wecom_link_name}
                  </a>
                ) : (
                  <span style={{ display: 'inline-block', color: '#ccc' }}>未添加企业微信</span>
                )
              ) : (
                val.children
              )}
            </ItemRow>
          );
        }
        return <ItemRow {...val} key={`${val.label}_${index}`} />;
      })}
    </div>
  );
};

const NORMAL_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const SAVE_NORMAL_FORMAT = 'YYYY-MM-DDTHH:mm:ss.ss';

type ReactEvent = React.KeyboardEvent<HTMLElement>;
type OptionValue = string | number;

type SelectData = {
  value: OptionValue;
  name: string | JSX.Element;
};
type EditableCellCommonProps = {
  name: string;
  editable?: boolean;
  onSave: (param: Record<string, CardFiledValue>, cb: () => void) => void;
  onKeyDownFocus?: (e: ReactEvent) => void;
} & FormComponentProps;

type ItemString = {
  type: 'text' | 'string';
  initialValue: string;
} & EditableCellCommonProps;
type ItemRadio = {
  type: 'radio';
  initialValue: OptionValue;
  selectData?: Array<SelectData>;
} & EditableCellCommonProps;

type ItemCheckbox = {
  type: 'check' | 'checkbox';
  initialValue: Array<OptionValue>;
  selectData?: Array<SelectData>;
} & EditableCellCommonProps;

type ItemDate = {
  type: 'datetime' | 'date';
  initialValue: string;
} & EditableCellCommonProps;

type ItemNumber = {
  type: 'number';
  initialValue: number;
} & EditableCellCommonProps;

type EditableCellProps = ItemString | ItemRadio | ItemCheckbox | ItemDate | ItemNumber;

type EditableCellType = React.FC<EditableCellProps>;

const EditableCell: EditableCellType = (props: EditableCellProps): JSX.Element => {
  const { editable = false, name, onSave, form, type, onKeyDownFocus } = props;
  const [editing, toggleEdit] = useState(false);
  const input = useRef<
    | React.ComponentRef<typeof Input>
    | React.ComponentRef<typeof TextAreaRef>
    | React.ComponentRef<typeof InputNumber>
    | null
  >(null);
  const { checkFunctionAndPerm } = useUpgrade();

  useEffect(() => {
    if (editing) {
      if (type === 'string' || type === 'text') {
        if (input.current) {
          input.current.focus();
        }
      }
    }
  }, [editing]);

  const onEditClick = useCallback(() => {
    if (!checkFunctionAndPerm('client_card', 'add_del_client_card')) {
      return;
    }
    if (editable) {
      toggleEdit(true);
    }
  }, [editable, checkFunctionAndPerm]);

  const save = (): void => {
    form.validateFields((error, values) => {
      if (!error) {
        const result = values;

        if (type === 'datetime') {
          if (!values[name].isValid()) {
            return false;
          }
          result[name] = `${values[name].utc().format(SAVE_NORMAL_FORMAT)}Z`;
        }
        if (type === 'radio') {
          // 如果值是undefined 表明是置空了
          if (result[name] === undefined) {
            result[name] = '';
          }
        }

        if (type === 'string') {
          result[name] = result[name].trim();
        }

        onSave(result, () => {
          toggleEdit(false);
        });
        return true;
      }
    });
  };

  const renderCellInput = (props: ItemString): JSX.Element => {
    const { editable = false, name, form, initialValue } = props;
    return editing ? (
      <FormItem style={{ margin: 0 }} className={style["form-item"]}>
        {form.getFieldDecorator(name, {
          initialValue,
        })(
          <Input
            size="small"
            ref={input as React.RefObject<React.ComponentRef<typeof Input>>}
            onPressEnter={save}
            onBlur={save}
          />,
        )}
      </FormItem>
    ) : (
      <div className={style["editable-cell-value-wrap"]} onClick={onEditClick}>
        <span>{initialValue}</span>
        {editable ? <EditOutlined /> : null}
      </div>
    );
  };
  const renderSelect = (props: ItemRadio): JSX.Element => {
    const { editable = false, name, form, initialValue, selectData = [] } = props;

    const getName = (value: OptionValue) => {
      const selctedItem = selectData.find(val => val.value === value);
      if (selctedItem) {
        return selctedItem.name;
      }
      return value;
    };
    return editing ? (
      <FormItem style={{ margin: 0 }} className={style["form-item"]}>
        {form.getFieldDecorator(name, {
          initialValue,
        })(
          <Select
            size="small"
            style={{ width: "100%" }}
            allowClear
            defaultOpen={true}
            onChange={() => {
              setTimeout(() => {
                save();
              }, 0);
            }}
            onBlur={() => {
              setTimeout(() => {
                save();
              }, 0);
            }}
          >
            {selectData.map((val) => {
              const { value, name } = val;
              return <Option key={value}>{name}</Option>;
            })}
          </Select>,
        )}
      </FormItem>
    ) : (
      <div className={style["editable-cell-value-wrap"]} onClick={onEditClick}>
        <span>{getName(initialValue)}</span>
        {editable ? <EditOutlined /> : null}
      </div>
    );
  };
  const renderSelectMultiple = (props: ItemCheckbox): JSX.Element => {
    const { editable = false, name, form, initialValue, selectData = [] } = props;

    const getName = (value: OptionValue) => {
      const selctedItem = selectData.find(val => val.value === value);
      if (selctedItem) {
        return selctedItem.name;
      }
      return value;
    };
    return editing ? (
      <FormItem style={{ margin: 0 }} className={style["form-item"]}>
        {form.getFieldDecorator(name, {
          initialValue:
            initialValue && _.isArray(initialValue) ? initialValue : [],
        })(
          <Select
            size="small"
            mode="multiple"
            style={{ width: "100%" }}
            defaultOpen={true}
            onBlur={() => {
              setTimeout(() => {
                save();
              }, 0);
            }}
          >
            {selectData.map((val) => {
              const { value, name } = val;
              return <Option key={value}>{name}</Option>;
            })}
          </Select>,
        )}
      </FormItem>
    ) : (
      <div className={style["editable-cell-value-wrap"]} onClick={onEditClick}>
        <span>
          {initialValue && _.isArray(initialValue)
            ? initialValue.map((val) => getName(val)).join(",")
            : ""}
        </span>
        {editable ? <EditOutlined /> : null}
      </div>
    );
  };

  const renderCellTimePicker = (props: ItemDate): JSX.Element => {
    const { editable = false, name, form, initialValue } = props;

    const timeValue = moment(initialValue).isValid() ? { initialValue: moment.utc(initialValue).local() } : {};
    return editing ? (
      <FormItem style={{ margin: 0 }} className={style["form-item"]}>
        {form.getFieldDecorator(name, {
          ...timeValue,
        })(
          <DatePicker
            size="small"
            open={true}
            format={NORMAL_FORMAT}
            onOpenChange={() => {
              setTimeout(() => {
                save();
                toggleEdit(false);
              });
            }}
          />,
        )}
      </FormItem>
    ) : (
      <div className={style["editable-cell-value-wrap"]} onClick={onEditClick}>
        <span>
          {initialValue && moment.isMoment(moment(initialValue))
            ? moment.utc(initialValue).local().format(NORMAL_FORMAT)
            : ""}
        </span>
        {editable ? <EditOutlined /> : null}
      </div>
    );
  };

  const renderCellTextArea = (props: ItemString): JSX.Element => {
    const { editable = false, name, form, initialValue } = props;

    return editing ? (
      <FormItem style={{ margin: 0 }} className={style["form-item"]}>
        {form.getFieldDecorator(name, {
          initialValue,
        })(
          <TextArea
            ref={
              input as React.RefObject<React.ComponentRef<typeof TextAreaRef>>
            }
            onBlur={save}
          />,
        )}
      </FormItem>
    ) : (
      <div className={style["editable-cell-value-wrap"]} onClick={onEditClick}>
        <span>{initialValue}</span>
        {editable ? <EditOutlined /> : null}
      </div>
    );
  };

  const renderCellNumber = (props: ItemNumber): JSX.Element => {
    const { editable = false, name, form, initialValue } = props;

    return editing ? (
      <FormItem style={{ margin: 0 }} className={style["form-item"]}>
        {form.getFieldDecorator(name, {
          initialValue,
        })(
          <InputNumber
            size="small"
            ref={
              input as React.RefObject<React.ComponentRef<typeof InputNumber>>
            }
            onPressEnter={save}
            onBlur={save}
          />,
        )}
      </FormItem>
    ) : (
      <div className={style["editable-cell-value-wrap"]} onClick={onEditClick}>
        <span>{initialValue}</span>
        {editable ? <EditOutlined /> : null}
      </div>
    );
  };

  // 类似都修改成新的类型
  const renderCell = (props: EditableCellProps) => {
    switch (props.type) {
      case 'string':
        return renderCellInput(props);
      case 'text':
        return renderCellTextArea(props);
      case 'radio':
        return renderSelect(props);
      case 'check':
      case 'checkbox':
        return renderSelectMultiple(props);
      case 'datetime':
      case 'date':
        return renderCellTimePicker(props);
      case 'number':
        return renderCellNumber(props);
      default:
        // 老数据有其他type!!!!!!!!!!
        return renderCellInput(props);
    }
  };

  // 激活编辑备注
  useKeydown((e: ReactEvent) => {
    if (onKeyDownFocus && onKeyDownFocus(e) && editable) {
      // 当出现过富文本过后，热键触发聚焦会把热键带入内容
      setTimeout(() => {
        toggleEdit(true);
      }, 0);
    }
  }, []);

  // change by wangxing 里面根据类型自己做判断显示，因为有的类型的是需要转换的
  return <>{renderCell(props)}</>;
};

const FormEditableCell = Form.create<EditableCellCommonProps>()(EditableCell);

export default { FormEditableCell, EditableCell, VisitorInfoList, ItemRow };
